/**
 * Shared client-side helper for kicking off the Shopify OAuth 2.0 flow.
 *
 * Used by the IntegrationsPage Shopify card's "Connect a store" button.
 *
 * Unlike QBO — where the Client ID is a single `VITE_QBO_CLIENT_ID`
 * baked in at build time — Shopify Client IDs live per-organization on
 * `integrations.metadata.client_id`. The caller passes it in here, and
 * we also forward it to `shopify-oauth-callback` so the Edge Function
 * uses the same one for code exchange (i.e. avoids the "two installs
 * for the same org with different apps" failure mode).
 *
 * CSRF defence: we stash a random state token in sessionStorage. The
 * matching `state` is validated by `ShopifyCallbackPage` after Shopify
 * redirects back, and again server-side by `shopify-oauth-callback`.
 */

export const SHOPIFY_OAUTH_STATE_KEY = 'shopify_oauth_state';
export const SHOPIFY_OAUTH_SHOP_KEY = 'shopify_oauth_shop';
export const SHOPIFY_REDIRECT_PATH = '/integrations/shopify/callback';

// Scopes Atlas's shopify-sync needs. If you add another read_* domain
// (e.g. read_customers, read_fulfillments), bump this — the operator
// will have to re-OAuth to grant the new permission.
export const SHOPIFY_SCOPES = ['read_orders', 'read_products', 'read_inventory'];

/**
 * Returns the redirect_uri Shopify will send the user back to after
 * they approve the install. This MUST also be registered in the
 * "Allowed redirection URL(s)" list under the app's dev-dashboard
 * settings. Shopify does an exact string match.
 */
export function shopifyRedirectUri(): string {
  return `${window.location.origin}${SHOPIFY_REDIRECT_PATH}`;
}

export interface StartShopifyOAuthArgs {
  clientId: string;
  /**
   * The store the operator wants to install on. Either the bare
   * subdomain ("eloo-designs") or the full host ("eloo-designs.myshopify.com");
   * we normalise it.
   */
  shopDomain: string;
}

/**
 * Begins the OAuth flow by redirecting the browser to Shopify. Returns
 * `null` on success (the caller's tab is about to navigate away) and a
 * human-readable error string if validation fails. Callers should
 * surface the error via toast.
 */
export function startShopifyOAuth(args: StartShopifyOAuthArgs): string | null {
  const clientId = args.clientId?.trim();
  if (!clientId) {
    return 'No Shopify Client ID configured. Configure app credentials first.';
  }

  const shop = normaliseShopDomain(args.shopDomain);
  if (!shop) {
    return 'Enter your Shopify store domain — for example "eloo-designs" or "eloo-designs.myshopify.com".';
  }

  const state = crypto.randomUUID();
  sessionStorage.setItem(SHOPIFY_OAUTH_STATE_KEY, state);
  sessionStorage.setItem(SHOPIFY_OAUTH_SHOP_KEY, shop);

  const url = new URL(`https://${shop}/admin/oauth/authorize`);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('scope', SHOPIFY_SCOPES.join(','));
  url.searchParams.set('redirect_uri', shopifyRedirectUri());
  url.searchParams.set('state', state);
  // Omit `grant_options[]` to request an *offline* token — the
  // long-lived shop-scoped token shopify-sync uses. `per-user` would
  // give us a short-lived online token tied to the operator's session,
  // which dies the moment they sign out of Shopify.
  window.location.href = url.toString();
  return null;
}

/**
 * Normalises whatever the operator pasted into a canonical
 * `<store>.myshopify.com` host. Returns null if it doesn't look like a
 * Shopify store domain at all.
 */
export function normaliseShopDomain(raw: string): string | null {
  if (!raw) return null;
  let value = raw.trim().toLowerCase();
  // Strip protocol + trailing slashes if the operator pasted a full URL.
  value = value.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  if (!value) return null;
  // Already fully-qualified?
  if (/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(value)) return value;
  // Bare subdomain.
  if (/^[a-z0-9][a-z0-9-]*$/.test(value)) return `${value}.myshopify.com`;
  return null;
}
